import type { Account, Conversation, Id, Member } from '@/model';
import { i18next } from '@/i18n';

const record = (value: unknown): Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const records = (value: unknown): Record<string, unknown>[] => Array.isArray(value) ? value.map(record) : [];
const text = (value: unknown): string | undefined => typeof value === 'string' ? value : undefined;
export const activeMembers = (conversation: Conversation): Member[] => conversation.members.filter(member => member.left_seq == null);
export const hasDefaultConversationTitle = (conversation: Conversation): boolean => !conversation.title?.trim() || conversation.title.trim() === 'Chat';
function copy(key: 'notes' | 'group' | 'direct' | 'deleted' | 'unknown'): string { return i18next.t(`common:conversationNames.${key}`); }
export function accountDisplayName(account: Account | undefined, alias?: string): string {
  return !account ? copy('unknown') : account.deleted ? copy('deleted') : alias ?? account.display_name;
}

/** App 0.7.0 ConversationRecord.memberNames: assistants override generic seat names, never with a viewer alias. */
export function conversationMemberNames(conversation: Conversation, accounts: ReadonlyMap<Id, Account>): Map<Id, string> {
  const members = activeMembers(conversation); const active = new Set(members.map(member => member.account_id));
  const names = new Map<Id, string>();
  for (const member of members) if (typeof member.chat_name === 'string' && member.chat_name && !names.has(member.account_id)) names.set(member.account_id, member.chat_name);
  const assistants = new Set<Id>();
  for (const assistant of records(conversation.assistants)) {
    const id = text(assistant.account_id); if (!id || !active.has(id) || assistants.has(id)) continue;
    assistants.add(id);
    names.set(id, text(assistant.chat_name)?.trim() || accountDisplayName(accounts.get(id)));
  }
  return names;
}

export function conversationIdentity(conversation: Conversation, viewer: Id, accounts: ReadonlyMap<Id, Account>) {
  const members = activeMembers(conversation);
  const others = members.filter(member => member.account_id !== viewer).sort((a, b) => a.account_id < b.account_id ? -1 : a.account_id > b.account_id ? 1 : 0);
  const profiles = others.flatMap(member => { const account = accounts.get(member.account_id); return account ? [account] : []; });
  const team = record(conversation.team); const agentId = text(team.agent_account_id);
  const hasTeam = !!agentId;
  const stock = team.kind === 'stock';
  const presentsAsTeam = stock || records(team.members).some(member => member.role === 'member');
  const knownAgents = new Set([agentId, ...records(team.members).map(member => text(member.account_id)), ...records(conversation.assistants).map(member => text(member.account_id))]);
  const agentChat = conversation.kind === 'group' && members.length === 2 && (hasTeam
    ? !presentsAsTeam && members.some(member => member.account_id === agentId)
    : members.some(member => knownAgents.has(member.account_id)));
  const keepsAgentIdentity = agentChat && hasTeam && !stock;
  const peerId = agentChat ? agentId : conversation.kind === 'direct' || conversation.kind === 'group' && members.length === 2 && hasDefaultConversationTitle(conversation) && !conversation.avatar_media_id
    ? others[0]?.account_id : undefined;
  const peer = peerId ? accounts.get(peerId) ?? null : null;
  return { members, profiles, team, agentId, peer, agentChat, keepsAgentIdentity };
}

/** App 0.7.0 ConversationTitle.make / ConversationNaming.title, shared by every projection. */
export function conversationTitle(conversation: Conversation, viewer: Id, accounts: ReadonlyMap<Id, Account>, aliases: ReadonlyMap<Id, string> = new Map()): string {
  const identity = conversationIdentity(conversation, viewer, accounts);
  const { members, profiles, team, agentId } = identity;
  if (identity.keepsAgentIdentity && agentId) {
    const defaults = new Set(['Chat', `solo-${text(team.agent) ?? 'unknown'}`, text(record(team.spec).name),
      ...records(team.roster).filter(member => member.account_id === agentId || member.role === 'leader').map(member => text(member.name))]);
    const own = conversation.title?.trim(); if (own && !defaults.has(own)) return own;
    // Solo titles follow Member.chat_name, not the assistant owner or a private alias.
    const seat = members.find(member => member.account_id === agentId && typeof member.chat_name === 'string' && member.chat_name);
    if (seat) return seat.chat_name as string;
    const profile = profiles.find(account => account.id === agentId);
    return profile ? accountDisplayName(profile) : team.agent === 'claude' ? 'Claude' : team.agent === 'codex' ? 'Codex' : 'Agent';
  }
  if (conversation.kind === 'self') return copy('notes');
  if (conversation.kind === 'direct') return profiles[0] ? accountDisplayName(profiles[0], aliases.get(profiles[0].id)) : copy('direct');
  if (conversation.kind === 'group' && members.length === 2 && hasDefaultConversationTitle(conversation) && !conversation.avatar_media_id && profiles[0]) {
    return accountDisplayName(profiles[0], aliases.get(profiles[0].id));
  }
  if (!hasDefaultConversationTitle(conversation) && conversation.title) return conversation.title;
  const chatNames = conversationMemberNames(conversation, accounts);
  const names = profiles.map(account => chatNames.get(account.id) ?? accountDisplayName(account, aliases.get(account.id)));
  if (!names.length) return copy('group');
  const shown = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${shown} +${names.length - 3}` : shown;
}

/** Search matches both account/alias and per-chat names without sharing one chat's title as a person's name. */
export function conversationSearchNames(conversation: Conversation, viewer: Id, accounts: ReadonlyMap<Id, Account>, aliases: ReadonlyMap<Id, string> = new Map()): string[] {
  const chatNames = conversationMemberNames(conversation, accounts);
  return [...new Set(activeMembers(conversation).filter(member => member.account_id !== viewer).flatMap(member => {
    const account = accounts.get(member.account_id); if (!account || account.deleted) return [];
    return [chatNames.get(account.id), accountDisplayName(account, aliases.get(account.id)), account.display_name].filter((name): name is string => !!name);
  }))];
}
